import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getBackgrounds, createBackground, updateBackground, deleteBackground, uploadAdminImage } from "@/api/admin.api";
import type { BackgroundImage } from "@/api/admin.api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Plus, Trash2, Upload, Image, Smartphone, Monitor, Save } from "lucide-react";

function PaddingEditor({ bg }: { bg: BackgroundImage }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    paddingTop: bg.paddingTop ?? 40,
    paddingBottom: bg.paddingBottom ?? 35,
    paddingX: bg.paddingX ?? 5,
    paddingTopDesktop: bg.paddingTopDesktop ?? 90,
    paddingBottomDesktop: bg.paddingBottomDesktop ?? 55,
    paddingXDesktop: bg.paddingXDesktop ?? 50,
  });

  const mutation = useMutation({
    mutationFn: (data: Partial<BackgroundImage>) => updateBackground(bg._id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-backgrounds"] }),
  });

  const handleSave = () => mutation.mutate(form);

  // Preview scale factors
  const mScaleY = 280 / 812;
  const mScaleX = 140 / 375;
  const dScaleY = 170 / 900;

  return (
    <div className="space-y-4">
      {/* Previews */}
      <div className="flex gap-3">
        {/* Mobile preview */}
        <div>
          <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Smartphone className="size-3" /> Mobil</p>
          <div style={{ width: 140, height: 280, position: "relative", borderRadius: 16, overflow: "hidden", border: "1px solid #e2e8f0" }}>
            <img src={bg.url} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "fill" }} />
            <div style={{
              position: "absolute",
              top: Math.round(form.paddingTop * mScaleY),
              bottom: Math.round(form.paddingBottom * mScaleY),
              left: Math.round(form.paddingX * mScaleX),
              right: Math.round(form.paddingX * mScaleX),
              border: "2px dashed rgba(59,130,246,0.7)",
              borderRadius: 4,
              background: "rgba(59,130,246,0.08)",
            }} />
          </div>
        </div>
        {/* Desktop preview */}
        <div>
          <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Monitor className="size-3" /> Desktop</p>
          <div style={{ width: 240, height: 170, position: "relative", borderRadius: 8, overflow: "hidden", border: "1px solid #e2e8f0" }}>
            <img src={bg.url} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
            <div style={{
              position: "absolute",
              top: Math.round(form.paddingTopDesktop * dScaleY),
              bottom: Math.round(form.paddingBottomDesktop * dScaleY),
              left: "50%",
              transform: "translateX(-50%)",
              width: `${Math.max(20, 60 - form.paddingXDesktop * 0.15)}%`,
              border: "2px dashed rgba(59,130,246,0.7)",
              borderRadius: 4,
              background: "rgba(59,130,246,0.08)",
            }} />
          </div>
        </div>
      </div>

      {/* Mobile padding */}
      <div>
        <p className="text-xs font-medium mb-1.5 flex items-center gap-1"><Smartphone className="size-3" /> Mobil Padding (px)</p>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Üst</Label>
            <Input type="number" min={0} max={200} value={form.paddingTop} onChange={(e) => setForm({ ...form, paddingTop: Number(e.target.value) })} className="h-8 text-sm" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Alt</Label>
            <Input type="number" min={0} max={200} value={form.paddingBottom} onChange={(e) => setForm({ ...form, paddingBottom: Number(e.target.value) })} className="h-8 text-sm" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Yan</Label>
            <Input type="number" min={0} max={200} value={form.paddingX} onChange={(e) => setForm({ ...form, paddingX: Number(e.target.value) })} className="h-8 text-sm" />
          </div>
        </div>
      </div>

      {/* Desktop padding */}
      <div>
        <p className="text-xs font-medium mb-1.5 flex items-center gap-1"><Monitor className="size-3" /> Desktop Padding (px)</p>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Üst</Label>
            <Input type="number" min={0} max={300} value={form.paddingTopDesktop} onChange={(e) => setForm({ ...form, paddingTopDesktop: Number(e.target.value) })} className="h-8 text-sm" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Alt</Label>
            <Input type="number" min={0} max={300} value={form.paddingBottomDesktop} onChange={(e) => setForm({ ...form, paddingBottomDesktop: Number(e.target.value) })} className="h-8 text-sm" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Yan</Label>
            <Input type="number" min={0} max={300} value={form.paddingXDesktop} onChange={(e) => setForm({ ...form, paddingXDesktop: Number(e.target.value) })} className="h-8 text-sm" />
          </div>
        </div>
      </div>

      <Button size="sm" className="w-full gap-1.5" onClick={handleSave} disabled={mutation.isPending}>
        <Save className="size-3.5" />
        {mutation.isPending ? "Kaydediliyor..." : "Kaydet"}
      </Button>
      {mutation.isSuccess && <p className="text-xs text-green-600">Kaydedildi</p>}
    </div>
  );
}

export function BackgroundsPage() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [sizeInfo, setSizeInfo] = useState<{ original: number; optimized: number } | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: backgrounds, isLoading } = useQuery({
    queryKey: ["admin-backgrounds"],
    queryFn: getBackgrounds,
  });

  const saveMutation = useMutation({
    mutationFn: () => createBackground({ name, url: previewUrl }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-backgrounds"] });
      setName("");
      setPreviewUrl("");
      setSizeInfo(null);
    },
  });

  const delMutation = useMutation({
    mutationFn: deleteBackground,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-backgrounds"] }),
  });

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadAdminImage(file);
      setPreviewUrl(result.url);
      setSizeInfo({ original: result.originalSize, optimized: result.optimizedSize });
      if (!name) setName(file.name.replace(/\.[^.]+$/, ""));
    } catch (err) {
      alert("Yükleme başarısız: " + ((err as Error).message || "Bilinmeyen hata"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="max-w-4xl relative">
      {/* Full overlay when uploading */}
      {uploading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="flex flex-col items-center gap-3 rounded-xl bg-card p-6 shadow-2xl">
            <svg className="size-8 animate-spin text-primary" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            <p className="font-medium">Görsel optimize ediliyor...</p>
          </div>
        </div>
      )}
      <h1 className="mb-6 text-2xl font-bold">Arkaplan Görselleri</h1>

      {/* Upload form */}
      <Card className="mb-6">
        <CardContent className="pt-5 space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label>İsim</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn: Akdeniz Mutfağı" />
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? (
                  <><svg className="mr-2 size-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Yükleniyor...</>
                ) : (
                  <><Upload className="mr-2 size-4" />Görsel Seç</>
                )}
              </Button>
            </div>
          </div>
          {previewUrl && (
            <div className="flex items-center gap-4">
              <img src={previewUrl} alt="" className="h-24 w-16 rounded-lg object-cover" />
              <div>
                {sizeInfo && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Orijinal: {(sizeInfo.original / 1024).toFixed(0)} KB → Optimize: <strong>{(sizeInfo.optimized / 1024).toFixed(0)} KB</strong>
                    <span className="ml-1 text-green-600">(%{(100 - (sizeInfo.optimized / sizeInfo.original) * 100).toFixed(0)} küçültüldü)</span>
                  </p>
                )}
                <Button onClick={() => saveMutation.mutate()} disabled={!name || saveMutation.isPending}>
                  <Plus className="mr-2 size-4" />
                  {saveMutation.isPending ? "Ekleniyor..." : "Ekle"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* List */}
      {isLoading ? (
        <p className="py-8 text-center text-muted-foreground">Yükleniyor...</p>
      ) : !backgrounds?.length ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Image className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">Henüz arkaplan görseli eklenmemiş</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {backgrounds.map((bg) => (
            <Card key={bg._id} className="overflow-hidden">
              <div className="relative cursor-pointer" onClick={() => setExpandedId(expandedId === bg._id ? null : bg._id)}>
                <img src={bg.url} alt={bg.name} className="h-40 w-full object-cover" />
                <div className="absolute bottom-2 right-2 rounded bg-black/50 px-2 py-0.5 text-xs text-white">
                  {expandedId === bg._id ? "Kapat" : "Padding Ayarla"}
                </div>
              </div>
              <CardContent className="pt-3 pb-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate">{bg.name}</span>
                  <ConfirmDialog
                    trigger={<Button variant="ghost" size="icon" className="size-7"><Trash2 className="size-4 text-destructive" /></Button>}
                    title="Görseli Sil"
                    description="Bu arkaplan görseli kalıcı olarak silinecek."
                    confirmLabel="Sil"
                    onConfirm={() => delMutation.mutate(bg._id)}
                  />
                </div>
                {expandedId === bg._id && <PaddingEditor bg={bg} />}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
